/**
 * Live room-score read seam. The relay sends a pre-resolved deadline table and a full transport
 * anchor; every read projects from relay time, so timers only request UI refreshes and never own
 * the clock.
 */
import { deriveScoreState, normalizeScoreTransport, scoreGrantsAt } from './scoreRuntime.js';
import { grantsFromWire } from './wire.js';
import { roomLeases } from './roomLeases.js';

function runtimeFromWire(raw) {
  if (!raw || !Array.isArray(raw.sections) || raw.sections.length === 0) return null;
  const sections = [];
  let cursor = 0;
  for (let index = 0; index < raw.sections.length; index += 1) {
    const section = raw.sections[index];
    const durationMs = Number(section?.durationMs);
    if (!Number.isFinite(durationMs) || durationMs <= 0) return null;
    const grants = grantsFromWire({ grants: section?.grants }) ?? {};
    const startMs = cursor;
    cursor += durationMs;
    sections.push({
      id: typeof section?.id === 'string' ? section.id : String(index),
      name: typeof section?.name === 'string' ? section.name : '',
      durationMs,
      startMs,
      endMs: cursor,
      grants,
    });
  }
  return { sections, totalMs: cursor };
}

export class RoomScore {
  #runtime = null;
  #transport = null;
  #allGrants = {};
  #listeners = new Set();
  #tick = null;

  get active() {
    return this.#runtime !== null && this.#transport !== null;
  }

  applyWire({ score, scoreTransport, allGrants } = {}) {
    const runtime = runtimeFromWire(score);
    if (runtime) this.#runtime = runtime;
    if (this.#runtime && scoreTransport && typeof scoreTransport === 'object') {
      this.#transport = normalizeScoreTransport(scoreTransport, roomLeases.relayNow() ?? 0);
    }
    const grants = grantsFromWire({ grants: allGrants });
    if (grants) this.#allGrants = grants;
    this.#announce();
  }

  noteRelayClock() {
    if (this.active) this.#armTick();
  }

  current() {
    if (!this.active) return null;
    return deriveScoreState(this.#runtime, this.#transport, roomLeases.relayNow());
  }

  grants() {
    if (!this.active) return null;
    return scoreGrantsAt(this.#runtime, this.#transport, roomLeases.relayNow(), this.#allGrants);
  }

  /**
   * The whole resolved section table — name, length and the grants each section carries. The
   * transport bar draws every section, not just the live one, so it needs the shape rather than
   * the derived pointer `current()` returns.
   */
  sections() {
    return this.#runtime?.sections ?? null;
  }

  /** Every voice's full holder set, which is what the free toggle hands the room. */
  allGrants() {
    return this.#allGrants;
  }

  subscribe(listener) {
    if (typeof listener !== 'function') return () => {};
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  reset() {
    this.#runtime = null;
    this.#transport = null;
    this.#allGrants = {};
    if (this.#tick !== null) clearTimeout(this.#tick);
    this.#tick = null;
    this.#announce(false);
  }

  #announce(rearm = true) {
    if (this.#tick !== null) clearTimeout(this.#tick);
    this.#tick = null;
    for (const listener of this.#listeners) {
      try { listener(); } catch (error) { console.warn('[room] score listener failed', error); }
    }
    if (rearm) this.#armTick();
  }

  #armTick() {
    if (this.#tick !== null || !this.active || typeof setTimeout !== 'function') return;
    const state = this.current();
    if (!state?.running) return;
    // Refresh on the next displayed second or the exact section boundary, whichever is first.
    // The callback owns no state: if it fires late, the next read still projects the right answer.
    const toSecond = 1000 - (state.positionMs % 1000 || 1000);
    const delay = Math.max(1, Math.min(toSecond || 1000, state.sectionRemainingMs || 1000));
    this.#tick = setTimeout(() => {
      this.#tick = null;
      this.#announce();
    }, delay);
  }
}

export const roomScore = new RoomScore();
